import PropTypes from 'prop-types';
// @mui
import { alpha, styled } from '@mui/material/styles';
import { Avatar, Box, Chip, Stack, Typography } from '@mui/material';
// utils
import { fDate, fToNow } from '../../../utils/formatTime';
import { fShortenNumber } from '../../../utils/formatNumber';
// components
import Iconify from '../../../components/iconify';
import { MotionInView, varFadeInUp } from '../../../components/animate';
// utils
import { DEFAULT_BLOG_AVATAR, DEFAULT_BLOG_COVER } from '../../../utils/blog';

// ----------------------------------------------------------------------

const META_LABEL = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.8,
  color: 'text.disabled',
};

const MetaItem = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  color: theme.palette.text.disabled,
  fontSize: 13,
  whiteSpace: 'nowrap',
}));

// ----------------------------------------------------------------------

// Article header: cover image, H1, lede, byline and the trust signals that
// matter most on a long-form read (author, published + updated dates,
// reading time, views and comments).
export default function BlogDetailHero({ post }) {
  const { title, excerpt, category, cover, readTime, view, comment, createdAt, updatedAt, author } = post ?? {};
  const authorName = author?.name ?? 'ASAP UAE';

  const wasUpdated = updatedAt && new Date(updatedAt) > new Date(createdAt);

  const stats = [
    { number: view, icon: 'eva:eye-fill', label: 'views' },
    { number: comment, icon: 'eva:message-circle-fill', label: 'comments' },
  ];

  return (
    <MotionInView variants={varFadeInUp}>
      <Box
        sx={{
          position: 'relative',
          borderRadius: 3,
          overflow: 'hidden',
          bgcolor: 'grey.200',
          aspectRatio: { xs: '16 / 10', sm: '21 / 9' },
          '& img': {
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          },
        }}
      >
        <Box
          component="img"
          src={cover}
          alt={title}
          decoding="async"
          onError={(event) => { event.currentTarget.src = DEFAULT_BLOG_COVER; }}
        />

        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: (theme) =>
              `linear-gradient(180deg, ${alpha(theme.palette.common.black, 0.05)} 0%, ${alpha(
                theme.palette.common.black,
                0.55
              )} 60%, ${alpha(theme.palette.common.black, 0.8)} 100%)`,
          }}
        />

        <Stack
          direction="row"
           spacing={1}
           sx={{ position: 'absolute', top: { xs: 16, sm: 24 }, left: { xs: 16, sm: 24 }, alignItems: 'center' }}
        >
          <Chip
            label={category}
            size="small"
            sx={{
              height: 24,
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              bgcolor: '#DF3E30',
              color: 'common.white',
            }}
          />
        </Stack>
      </Box>

      <Box sx={{ pt: { xs: 3, md: 4 } }}>
        <Typography
          component="h1"
          variant="h3"
          sx={{
            color: 'text.primary',
            maxWidth: 960,
            fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.75rem' },
            lineHeight: 1.2,
          }}
        >
          {title}
        </Typography>

        <Typography
          variant="subtitle1"
          sx={{
            mt: 2,
            color: 'text.secondary',
            maxWidth: 820,
            fontSize: { xs: 16, md: 18 },
            lineHeight: 1.7,
          }}
        >
          {excerpt}
        </Typography>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
           spacing={{ xs: 2, sm: 3 }}
           sx={{ mt: 3, alignItems: { xs: 'flex-start', sm: 'center' } }}
        >
          <Stack direction="row" spacing={1.5} sx={{ minWidth: 0, alignItems: 'center' }}>
            <Avatar
              alt={authorName}
              src={author?.avatarUrl}
              imgProps={{ onError: (event) => { event.currentTarget.src = DEFAULT_BLOG_AVATAR; } }}
              sx={{ width: 44, height: 44 }}
            >
              {authorName.charAt(0)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                {authorName}
              </Typography>
              {/* The backend user model has no role field yet, so the byline
              shows the role only when the payload provides one. */}
              {author?.role && (
                <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                  {author.role}
                </Typography>
              )}
            </Box>
          </Stack>

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: { xs: 1.5, sm: 2.5 },
            }}
          >
            <MetaItem>
              <Iconify icon="eva:calendar-outline" sx={{ width: 16, height: 16 }} />
              <Box>
                <Box component="span" sx={META_LABEL}>
                  Published
                </Box>
                {fDate(createdAt)}
              </Box>
            </MetaItem>

            {wasUpdated && (
              <MetaItem>
                <Iconify icon="eva:refresh-outline" sx={{ width: 16, height: 16 }} />
                <Box>
                  <Box component="span" sx={META_LABEL}>
                    Updated
                  </Box>
                  {fToNow(updatedAt)}
                </Box>
              </MetaItem>
            )}

            <MetaItem>
              <Iconify icon="eva:clock-outline" sx={{ width: 16, height: 16 }} />
              <Box>
                <Box component="span" sx={META_LABEL}>
                  Reading time
                </Box>
                {readTime} min read
              </Box>
            </MetaItem>

            {stats.map((stat) => (
              <MetaItem key={stat.icon}>
                <Iconify icon={stat.icon} sx={{ width: 16, height: 16 }} />
                <Box>
                  <Box component="span" sx={META_LABEL}>
                    {stat.label}
                  </Box>
                  {fShortenNumber(stat.number)}
                </Box>
              </MetaItem>
            ))}
          </Box>
        </Stack>
      </Box>
    </MotionInView>
  );
}

BlogDetailHero.propTypes = {
  post: PropTypes.object.isRequired,
};
